import React from 'react'
import clsx from 'clsx'
import { CircularProgress } from '@mui/material'
import { useDrag, useDrop } from 'react-dnd'
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux'

import DragIcon from '../../../../components/icons/DragIcon'
import FileArrowDown from '../../../../components/icons/FileArrowDown'
import FolderIcon from '../../../../components/icons/FolderIcon'
import FileIcon from '../../../../components/icons/FileIcon'

import ArticleTitle from '../ArticleTitle'
import ArticleOptionsMenu from '../../../../components/molecules/ArticleOptionsMenu'

import { moveArticle, selectArticle } from '../../../../slices/article'
import useStyles from './styles'

const ITEM_TYPE = 'ARTICLE_ITEM'

const ArticleItem = ({ item, parent, depth, expanded, toggle, zIndex }) => {
  const dispatch = useAppDispatch()
  const { root, articles, selected, loading } = useAppSelector(
    state => state.article
  )

  const onDrop = React.useCallback(
    after => dropItem => {
      setHovering(false)

      const source = dropItem.id
      const target = item.id
      if (source === target) {
        return
      }

      const sourceParent = dropItem.parent
      const targetParent = parent

      dispatch(moveArticle(source, target, sourceParent, targetParent, after))
    },
    [item.id, dispatch, parent]
  )

  const [{ opacity }, dragRef, previewRef] = useDrag({
    item: { type: ITEM_TYPE, id: item.id, parent },
    collect: monitor => ({
      opacity: monitor.isDragging() ? 0.4 : 1,
      dragging: !!monitor.isDragging()
    })
  })

  const [{ isOver: isOverTop }, dropTop] = useDrop({
    accept: ITEM_TYPE,
    drop: onDrop(false),
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const [{ isOver: isOverBottom }, dropBottom] = useDrop({
    accept: ITEM_TYPE,
    drop: onDrop(true),
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const [hovering, setHovering] = React.useState(false)
  const isSelected = React.useMemo(
    () => item.id === selected,
    [item.id, selected]
  )
  const isHovering = React.useMemo(
    () => hovering && item.type === 'folder',
    [hovering, item.type]
  )
  const oldOrder = React.useRef(item.order)

  const classes = useStyles({ depth: 0, isOverTop, isOverBottom })

  // Stop hovering when a re-order happens
  React.useEffect(() => {
    if (item.order === oldOrder.current) {
      setHovering(false)
    }
  }, [item.order, item, root, articles])

  React.useEffect(() => {
    oldOrder.current = item.order
  }, [item.order])

  const startHovering = React.useCallback(() => {
    setHovering(true)
  }, [])

  const endHovering = React.useCallback(() => {
    setHovering(false)
  }, [])

  return (
    <>
      <div
        onMouseEnter={startHovering}
        onMouseLeave={endHovering}
        className={classes.container}
        style={{ zIndex }}
      >
        <div ref={previewRef} className={classes.container} style={{ opacity }}>
          <div className={classes.root}>
            {hovering ? (
              <div ref={dragRef} className={classes.drag}>
                <DragIcon />
              </div>
            ) : (
              <div className={classes.emptyDrag} />
            )}

            <div className={classes.arrow}>
              {(expanded || loading.includes(item.id)) && !articles[item.id] ? (
                <CircularProgress className={classes.loading} />
              ) : (
                item.total_items > 0 && (
                  <FileArrowDown
                    onClick={() => {
                      toggle(item.id)
                    }}
                    className={classes.expandArrow}
                    style={{
                      transform: `rotate(${expanded ? 90 : 0}deg)`
                    }}
                  />
                )
              )}
            </div>

            <div
              className={clsx([classes.icon, isSelected && classes.selected])}
            >
              {item.type === 'folder' ? <FolderIcon /> : <FileIcon />}
            </div>

            <div
              onClick={() => {
                dispatch(selectArticle(item.id))
                if (item.type === 'folder') {
                  toggle(item.id)
                }
              }}
              className={clsx([
                classes.titleArea,
                isSelected && classes.selected
              ])}
            >
              <ArticleTitle depth={depth}>{item.title}</ArticleTitle>

              {item.type === 'folder' && (
                <div className={classes.count}>({item.total_items})</div>
              )}
            </div>

            {isHovering && (
              <ArticleOptionsMenu
                smallIcon
                articleId={item.id}
                className={classes.menu}
              />
            )}
          </div>
        </div>

        <div ref={dropTop} className={clsx(classes.drop, classes.dropTop)} />

        <div
          ref={dropBottom}
          className={clsx(classes.drop, classes.dropBottom)}
        />
      </div>
    </>
  )
}

export default ArticleItem
